import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  ArrowUpRight,
  FileSpreadsheet,
  Boxes,
  TrendingUp,
  AlertTriangle,
  Target,
  Briefcase,
  ExternalLink,
  LineChart as LineChartIcon,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";

export default function DashboardPage() {
  const [list, setList] = useState([]);
  const [summary, setSummary] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/dispensations"),
      api.get("/dashboard/summary"),
      api.get("/comparison/timeline").catch(() => ({ data: null })),
    ])
      .then(([d, s, tl]) => {
        setList(d.data);
        setSummary(s.data);
        setTimeline(tl.data);
      })
      .finally(() => setLoading(false));
  }, []);

  const totalUnits = (summary?.total_units ?? 0).toLocaleString("es-ES");
  const totalLines = (summary?.total_lines ?? 0).toLocaleString("es-ES");
  const last = summary?.last_period;
  const fmtEur = (n) =>
    Number(n || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

  // Timeline para el chart de evolución (cuando hay 2+ estudios)
  const timelineChart = useMemo(() => {
    const items = timeline?.timeline || [];
    if (items.length < 2) return null;
    return items.map((t) => {
      const efgU = (t.marca_units_pm || 0) + (t.generico_units_pm || 0);
      const efgE = (t.marca_euros_pm || 0) + (t.generico_euros_pm || 0);
      const pctU = efgU > 0 ? (t.generico_units_pm / efgU) * 100 : 0;
      const pctE = efgE > 0 ? (t.generico_euros_pm / efgE) * 100 : 0;
      return {
        label: t.label || t.period_start || t.filename,
        "% Genérico (uds)": Number(pctU.toFixed(2)),
        "% Genérico (€)": Number(pctE.toFixed(2)),
      };
    });
  }, [timeline]);

  return (
    <div className="p-8 lg:p-12 max-w-[1400px]" data-testid="dashboard-page">
      {/* Hero · vídeo */}
      <div className="relative mb-10 rounded-2xl overflow-hidden bg-slate-950 shadow-xl border border-slate-200" data-testid="dashboard-hero-video">
        <video
          src="/video/farmaclass-panel.mp4"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className="w-full h-auto max-h-[380px] object-cover block"
          aria-hidden="true"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-slate-950/75 via-slate-950/25 to-transparent" />
        <div className="absolute inset-0 flex flex-col justify-center px-8 lg:px-12 max-w-2xl pointer-events-none">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-sky-300 mb-2">FarmaClass</div>
          <h2 className="font-display text-2xl lg:text-3xl font-extrabold tracking-tight text-white leading-tight">
            Tu farmacia, en un panel.
          </h2>
          <p className="text-sm text-white/70 mt-2 max-w-lg">
            Convierte tus dispensaciones en decisiones que aumentan margen — sin perder un solo cliente.
          </p>
        </div>
      </div>

      <header className="mb-12">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">
          Panel · Visión general
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter text-slate-950">
          Dispensación EFG
        </h1>
        <p className="text-slate-500 mt-3 max-w-2xl">
          Sube un archivo Excel o TXT con códigos nacionales (CN &gt; 600.000) y unidades para
          obtener una clasificación entre marca y genérico, semaforización por grado de
          dispensación y desglose por laboratorio.
        </p>
      </header>

      {last && (
        <div
          data-testid="kpi-solo-marca-ratio"
          className="bg-gradient-to-br from-slate-950 to-slate-800 text-white rounded-lg p-6 mb-6 border border-slate-900"
        >
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-300 mb-3">
                <Target className="h-3 w-3" strokeWidth={2} />
                Pendiente de convertir · {last.label}
              </div>
              <div className="flex items-baseline gap-4 flex-wrap">
                <span className="font-display text-6xl font-bold tracking-tighter font-mono-num">
                  {last.solo_marca_ratio_pct}%
                </span>
                <div className="text-sm text-slate-300 max-w-md">
                  del volumen de marca dispensada está en moléculas <strong className="text-white">SOLO MARCA</strong> (sin sustitución a genérico iniciada).
                </div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-x-6 gap-y-1 text-right">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">€ Solo Marca</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">€ Marca total</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">Moléculas</div>
              <div className="font-mono-num font-bold text-rose-300">{fmtEur(last.solo_marca_euros)}</div>
              <div className="font-mono-num font-bold">{fmtEur(last.marca_euros)}</div>
              <div className="font-mono-num font-bold">{last.solo_marca_molecules}</div>
            </div>
          </div>
          <div className="mt-4 h-2 w-full bg-slate-700 rounded-full overflow-hidden">
            <div
              className="bg-rose-500 h-full transition-all"
              style={{ width: `${Math.min(100, last.solo_marca_ratio_pct)}%` }}
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
        <Kpi label="Dispensaciones cargadas" value={summary?.dispensations ?? list.length} icon={FileSpreadsheet} testid="kpi-dispensations" />
        <Kpi label="Unidades totales" value={totalUnits} icon={TrendingUp} testid="kpi-units" />
        <Kpi label="Líneas analizadas" value={totalLines} icon={Boxes} testid="kpi-lines" />
        <Kpi label="Catálogo · CN" value={(summary?.catalog_count ?? 0).toLocaleString("es-ES")} icon={AlertTriangle} testid="kpi-catalog" />
      </div>

      {timelineChart && timelineChart.length >= 2 && (
        <div className="bg-white border border-slate-200 rounded-lg p-6 mb-12" data-testid="dashboard-timeline-chart">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-1 flex items-center gap-2">
                <LineChartIcon className="h-3.5 w-3.5" /> Evolución mensual
              </div>
              <h2 className="font-display text-2xl font-semibold tracking-tight">Dispensación de genérico</h2>
              <p className="text-sm text-slate-500 mt-1">Porcentaje de genérico (universo EFG) sobre unidades y euros, mes a mes.</p>
            </div>
            <Link to="/comparison" className="text-xs font-bold text-slate-700 hover:text-sky-600 inline-flex items-center gap-1">
              Ver comparativa completa <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={timelineChart} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" stroke="#64748b" fontSize={12} />
              <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} stroke="#64748b" fontSize={12} />
              <Tooltip formatter={(v) => `${Number(v).toFixed(2)}%`} />
              <Legend />
              <Line type="monotone" dataKey="% Genérico (uds)" stroke="#10b981" strokeWidth={2.5} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="% Genérico (€)" stroke="#0ea5e9" strokeWidth={2.5} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-1">Recientes</div>
            <h2 className="font-display text-2xl font-semibold tracking-tight">Últimas cargas</h2>
          </div>
          <Link
            to="/upload"
            data-testid="new-upload-cta"
            className="inline-flex items-center gap-2 bg-slate-950 text-white px-4 py-2.5 rounded-md text-sm font-medium hover:bg-slate-800 transition-all"
          >
            Subir dispensación
            <ArrowUpRight className="h-4 w-4" strokeWidth={2} />
          </Link>
        </div>

        {loading ? (
          <div className="p-12 text-center text-slate-400">Cargando…</div>
        ) : list.length === 0 ? (
          <div className="p-16 text-center">
            <div className="bg-grid h-32 -mx-px mb-8 opacity-60" />
            <p className="text-slate-500 mb-1">Aún no has cargado dispensaciones.</p>
            <p className="text-sm text-slate-400">Sube tu primer Excel/TXT para empezar.</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-200">
            {list.slice(0, 8).map((d) => (
              <Link
                key={d.id}
                to={`/dispensations/${d.id}`}
                data-testid={`dispensation-row-${d.id}`}
                className="flex items-center justify-between p-5 hover:bg-slate-50 transition-colors"
              >
                <div>
                  <div className="font-medium text-slate-900">{d.label || d.filename}</div>
                  <div className="text-sm text-slate-500 mt-0.5 font-mono-num">
                    {d.period_start} → {d.period_end}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono-num text-lg font-semibold">{(d.total_units || 0).toLocaleString("es-ES")}</div>
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-500">unidades</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Services CTA banner */}
      <Link
        to="/servicios"
        className="mt-10 block group relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 text-white p-8 hover:shadow-2xl transition-shadow"
        data-testid="services-banner"
      >
        <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-sky-500/20 blur-3xl" />
        <div className="absolute -left-10 -bottom-10 h-48 w-48 rounded-full bg-emerald-500/10 blur-3xl" />
        <div className="relative flex flex-wrap items-center justify-between gap-6">
          <div className="flex items-start gap-4 max-w-2xl">
            <div className="h-12 w-12 rounded-2xl bg-white/10 grid place-items-center">
              <Briefcase className="h-6 w-6 text-sky-300" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-sky-300 font-bold">
                Archiduque Consulting
              </div>
              <div className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight mt-1">
                Llevamos tu farmacia al siguiente nivel
              </div>
              <p className="text-slate-300 text-sm mt-2 max-w-xl">
                +650 estudios, ~30.000 € de rentabilidad media anual y +1.000 clientes satisfechos.
                Estudio EFG, gestión de inventario, marketing farmacéutico, dirección por objetivos…
              </p>
            </div>
          </div>
          <div className="inline-flex items-center gap-2 bg-white text-slate-900 font-bold px-6 py-3 rounded-full group-hover:bg-sky-500 group-hover:text-white transition-colors">
            Ver servicios <ExternalLink className="h-4 w-4" />
          </div>
        </div>
      </Link>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, testid }) {
  return (
    <div data-testid={testid} className="kpi-card bg-white border border-slate-200 rounded-lg p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200">
      <div className="flex items-start justify-between mb-6">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{label}</div>
        <Icon className="h-4 w-4 text-slate-400" strokeWidth={1.5} />
      </div>
      <div className="font-display text-4xl font-bold tracking-tighter font-mono-num">{value}</div>
    </div>
  );
}
